import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController } from '@ionic/angular/lazy';
import { RegisterService } from 'src/app/services/register.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { ObserverService } from 'src/app/services/observer.service';
import moment from 'moment';
import { DateTimePickerPage } from 'src/app/modals/date-time-picker/date-time-picker.page';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { StorageService } from 'src/app/services/storage.service';
import { UserService } from 'src/app/services/user.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-parked-cart-filter',
    templateUrl: './parked-cart-filter.page.html',
    styleUrls: ['./parked-cart-filter.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ParkedCartFilterPage implements OnInit {
    @Input() date_from;
    @Input() date_to;
    @Input() location_id;
    @Input() register_id;
    @Input() timeframe;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    filter_params = {};
    registers = [];
    locations = [];
    moment = moment;
    formatted_date_from = null;
    formatted_date_to = null;
    date_modal_css_class = '';

    timeframe_options: any = { header: 'Timeframe' };
    register_options: any = { header: 'Register' };
    location_options: any = { header: 'Location' };

    // modal flags
    date_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private registerService: RegisterService,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService,
        private connectedAccountService: ConnectedAccountService,
        private storageService: StorageService,
        private userService: UserService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.date_modal_css_class = 'backdrop';
        }

        this.loadData().then(async () => {

            if(this.timeframe) {

                this.filter_params['timeframe'] = this.timeframe;

                if(this.filter_params['timeframe'] == 'custom') {

                    if(this.date_from || this.date_to) {
                        this.storageService.get("connected_account_id").then((connected_account_id) => {
                            if(connected_account_id) {
                                this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account: any) => {
                                    if(connected_account['user_id']) {
                                        this.userService.getUser(connected_account['user_id']).then((user: any) => {
                                            if(this.date_from) {
                                                this.filter_params['date_from'] = this.moment.utc(this.date_from).tz(user.timezone.code).toISOString(true);
                                                this.formatted_date_from = this.moment(this.filter_params['date_from']).format("D MMM YYYY, HH:mm");
                                            }
                                            if(this.date_to) {
                                                this.filter_params['date_to'] = this.moment.utc(this.date_to).tz(user.timezone.code).toISOString(true);
                                                this.formatted_date_to = this.moment(this.filter_params['date_to']).format("D MMM YYYY, HH:mm");
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }

                }
            }

            if(this.register_id) {
                this.filter_params['register_id'] = this.register_id;
            }

            if(this.location_id) {
                this.filter_params['location_id'] = this.location_id;
            }

        })
        .catch(() => { });

        this.observerService.subscribe('network_status_changed|parked_cart_filter_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to filter parked carts, please check your network connection and try again.");
                }
            }
        });

    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading registers").then(() => {
                this.loadRegisters().then(() => {
                    this.loading_spinner.message = "Loading locations";
                    this.loadLocations().then(() => {
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadRegisters() {
        return new Promise<void>((resolve, reject) => {
            this.registers = [];
            this.registerService.getRegisters().then((registers:[]) => {
                if(registers) {
                    this.registers = registers;
                    let register_found = false
                    for(let i = 0; i < this.registers.length; i++) {
                        if(this.registers[i].id == this.filter_params['register_id']) {
                            register_found = true;
                            break
                        }
                    }
                    if(!register_found) {
                        delete this.filter_params['register_id'];
                    }
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadLocations() {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getLocations().then((locations: []) => {
                    if(locations) {
                        this.locations = locations;
                        let location_found = false
                        for(let i = 0; i < this.locations.length; i++) {
                            if(this.locations[i].id == this.filter_params['location_id']) {
                                location_found = true;
                                break
                            }
                        }
                        if(!location_found) {
                            delete this.filter_params['location_id'];
                        }
                    }
                    resolve();
                })
                .catch((error) => {
                    if(error) {
                        if(error.status) {
                            if(error.status == 400) {
                                this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to retrieve a list of locations.");
                            }
                            else if(error.status == 403) {
                                this.presentAlert("Insufficient permissions", "You don't have permission to view location.");
                            }
                            else if(error.status == 404) {
                                this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve a list of locations at this time.");
                            }
                            else if(error.status == 0) {
                                this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                            }
                        }
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    filter() {

        let validated = true;

        if(this.filter_params["timeframe"]) {
            
            if(this.filter_params['timeframe'] == "today") {
                this.filter_params['date_from'] = this.moment().startOf("day").utc().format();
                this.filter_params['date_to'] = this.moment().endOf("day").utc().format();
            }
            else if(this.filter_params['timeframe'] == "yesterday") {
                this.filter_params['date_from'] = this.moment().startOf("day").subtract(1, 'days').utc().format();
                this.filter_params['date_to'] = this.moment().endOf("day").subtract(1, 'days').utc().format();
            }
            else if(this.filter_params['timeframe'] == "this_week") {
                this.filter_params['date_from'] = this.moment().startOf("isoWeek").utc().format();
                this.filter_params['date_to'] = this.moment().endOf("isoWeek").utc().format();
            }
            else if(this.filter_params['timeframe'] == "last_week") {
                this.filter_params['date_from'] = this.moment().startOf("isoWeek").subtract(1, 'weeks').utc().format();
                this.filter_params['date_to'] = this.moment().endOf("isoWeek").subtract(1, 'weeks').utc().format();
            }
            else if(this.filter_params['timeframe'] == "this_month") {
                this.filter_params['date_from'] = this.moment().startOf("month").utc().format();
                this.filter_params['date_to'] = this.moment().endOf("month").utc().format();
            }
            else if(this.filter_params['timeframe'] == "last_month") {
                this.filter_params['date_from'] = this.moment().startOf("month").subtract(1, 'months').utc().format();
                this.filter_params['date_to'] = this.moment().endOf("month").subtract(1, 'months').utc().format();
            }
            else if(this.filter_params['timeframe'] == "custom") {

                if( (this.filter_params['date_from'] == null || this.filter_params['date_from'].trim() === "") && (this.filter_params['date_to'] == null || this.filter_params['date_to'].trim() === "") ) {
                    validated = false;
                    this.presentAlert("Missing dates", "Please select a From date and/or a To date when using a Custom timeframe.");
                }
                else {
                    if(this.filter_params['date_from']) {
                        this.filter_params['date_from'] = this.moment(this.filter_params['date_from']).utc().format();
                    }
                    if(this.filter_params['date_to']) {
                        this.filter_params['date_to'] = this.moment(this.filter_params['date_to']).utc().format();
                    }
                }

            }

        }

        if(validated) {
            this.modalController.dismiss(this.filter_params);
        }

    }

    clearTimeframe() {

        if(this.filter_params['timeframe'] == "custom") {

            if(this.filter_params['date_from']) {
                delete this.filter_params["date_from"];
                this.formatted_date_from = null;
            }
            if(this.filter_params['date_to']) {
                delete this.filter_params["date_to"];
                this.formatted_date_to = null;
            }

        }

        delete this.filter_params['timeframe'];

    }

    clearRegister() {
        delete this.filter_params['register_id'];
    }

    clearLocation() {
        delete this.filter_params['location_id'];
    }

    clearFilters() {
        this.presentSpinner("Clearing filters").then(() => {

            if(this.filter_params['timeframe']) {
                delete this.filter_params["timeframe"];
            }
            if(this.filter_params['date_from']) {
                delete this.filter_params["date_from"];
                this.formatted_date_from = null;
            }
            if(this.filter_params['date_to']) {
                delete this.filter_params["date_to"];
                this.formatted_date_to = null;
            }
            if(this.filter_params['register_id']) {
                delete this.filter_params["register_id"];
            }
            if(this.filter_params['location_id']) {
                delete this.filter_params["location_id"];
            }

            this.spinner.dismiss();

        });
    }

    async presentFromDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: (this.filter_params['date_from'] ? this.filter_params['date_from'] : ""),
                    format: "date-time",
                    clear_date: true
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    if(data.data == "date_deleted") {
                        delete this.filter_params["date_from"];
                        this.formatted_date_from = null;
                    }
                    else {
                        this.filter_params['date_from'] = data.data['date'];
                        this.formatted_date_from = this.moment(this.filter_params['date_from']).format("D MMM YYYY, HH:mm");
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentToDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: (this.filter_params['date_to'] ? this.filter_params['date_to'] : ""),
                    format: "date-time",
                    clear_date: true
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    if(data.data == "date_deleted") {
                        delete this.filter_params["date_to"];
                        this.formatted_date_to = null;
                    }
                    else {
                        this.filter_params['date_to'] = data.data['date'];
                        this.formatted_date_to = this.moment(this.filter_params['date_to']).format("D MMM YYYY, HH:mm");
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

}
